// Catálogo de productos propio del módulo Aplicaciones.
// inv_productos es la fuente única (VFP queda como referencia histórica).
// Permite ver/editar productos con auditoría de cambios para certificación G.A.P.
//
// Acceso: usuarios con perm_vob (ingenieros/admin). Bodegueros no acceden.

import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { supabase } from '../../supabase';
import { buildApiUrl } from '../../utils/apiUrl';
import { useEmpresaId } from '../../context/EmpresaContext';
import { Boxes, Loader2, Search, Pencil, History, X, Save, ChevronDown, ChevronLeft, ChevronRight, Plus, ListTree, FileSpreadsheet } from 'lucide-react';
import { SortableTh, sortRows, useTableSort } from '../../utils/tableSort';

interface Producto {
  id: number;
  codigo: string;
  descripcion: string;
  descripcion_detallada: string | null;
  nombre_app: string | null;
  unidad_medida: string | null;
  stock_actual: number | null;
  stock_minimo: number | null;
  costo_promedio: number | null;
  moneda_costo: 'CRC' | 'USD' | string | null;
  precio_venta: number | null;
  activo: boolean;
  // VFP (legacy, solo lectura — referencia histórica)
  vfp_grupo_quimico_id: string | null;
  vfp_grupo_quimico_nom: string | null;
  vfp_linea_id: string | null;
  vfp_linea_nombre: string | null;
  vfp_bodega: string | null;
  vfp_aplicacion: string | null;
  vfp_sync_at: string | null;
  bodega_id: number | null;
  // Clasificación agronómica (editables en aplicaciones, se propagan a plan y boletas)
  justificacion: string | null;
  departamento: string | null;
  ingrediente_activo: string | null;
  ingrediente_activo_id: number | null;
  departamento_id: number | null;
  // Modalidad de aplicación (Foliar, Boom, etc.)
  modalidad_aplicacion_id: number | null;
  modalidad_aplicacion_cod: string | null;
  // Nombre corto para impresión en boleta
  nombre_boleta: string | null;
  // Operativos
  latencia_dias: number | null;
  reingreso: string | null;
  observaciones: string | null;
  // G.A.P.
  registro_mag: string | null;
  vencimiento_etiqueta: string | null;
  banda_toxicologica: string | null;
  banda_color: string | null;
  // Seguridad
  epi_requerido: string[] | null;
  restricciones_clima: string | null;
  zona_buffer_m: number | null;
  // Reglas
  dosis_max_ha: number | null;
  aplicaciones_max_anio: number | null;
  requiere_autorizacion: boolean;
  // Archivos
  etiqueta_pdf_url: string | null;
  msds_pdf_url: string | null;
  // Notas
  notas_agronomicas: string | null;
  editado_por: string | null;
  editado_at: string | null;
  // true = agroquímico/insumo real que usa Aplicaciones. false = aparece en el
  // catálogo compartido pero no es del módulo (ej. empaque, insumo de otra área).
  pertenece_aplicaciones: boolean;
}

interface Linea { id: string; nombre: string }

type ProductoSortKey = 'codigo' | 'descripcion' | 'linea' | 'medida' | 'stock' | 'costo' | 'moneda' | 'reingreso' | 'banda' | 'mag';
const LINEA_SIN_ASIGNAR = '__sin_linea__';

function productoNuevo(): Producto {
  return {
    id: 0, codigo: '', descripcion: '', descripcion_detallada: null, nombre_app: null,
    unidad_medida: null, stock_actual: 0, stock_minimo: 0, costo_promedio: 0, moneda_costo: 'CRC', precio_venta: 0, activo: true,
    vfp_grupo_quimico_id: null, vfp_grupo_quimico_nom: null, vfp_linea_id: null, vfp_linea_nombre: null,
    vfp_bodega: '1', vfp_aplicacion: null, vfp_sync_at: null, bodega_id: null, justificacion: null,
    departamento: null, ingrediente_activo: null, ingrediente_activo_id: null, departamento_id: null,
    modalidad_aplicacion_id: null, modalidad_aplicacion_cod: null, nombre_boleta: null, latencia_dias: null,
    reingreso: null, observaciones: null, registro_mag: null, vencimiento_etiqueta: null, banda_toxicologica: null,
    banda_color: null, epi_requerido: [], restricciones_clima: null, zona_buffer_m: null, dosis_max_ha: null,
    aplicaciones_max_anio: null, requiere_autorizacion: false, etiqueta_pdf_url: null, msds_pdf_url: null,
    notas_agronomicas: null, editado_por: null, editado_at: null,
    pertenece_aplicaciones: true,
  };
}

interface AuditFila {
  id: number;
  campo: string;
  valor_antes: string | null;
  valor_despues: string | null;
  cambiado_por: string | null;
  cambiado_at: string;
}

function fmtN(v: number | null | undefined, dec = 2): string {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('es-CR', { minimumFractionDigits: dec, maximumFractionDigits: dec });
}

const EPI_OPCIONES = ['Mascarilla', 'Guantes', 'Botas', 'Traje impermeable', 'Lentes', 'Casco'];

function fmtMoney(v: number | null | undefined, moneda?: string | null, dec = 4): string {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  const curr = moneda === 'USD' ? 'USD' : 'CRC';
  const prefix = curr === 'USD' ? '$' : '₡';
  return `${prefix}${n.toLocaleString('es-CR', { minimumFractionDigits: dec, maximumFractionDigits: dec })}`;
}

const PRODUCTOS_SELECT = `
  id, codigo, descripcion, descripcion_detallada, unidad_medida,
  stock_actual, stock_minimo, costo_promedio, moneda_costo, precio_venta, activo,
  vfp_grupo_quimico_id, vfp_grupo_quimico_nom,
  vfp_linea_id, vfp_linea_nombre, vfp_bodega, vfp_aplicacion, bodega_id,
  latencia_dias, reingreso, observaciones, vfp_sync_at,
  registro_mag, vencimiento_etiqueta, banda_toxicologica, banda_color,
  epi_requerido, restricciones_clima, zona_buffer_m,
  dosis_max_ha, aplicaciones_max_anio, requiere_autorizacion,
  etiqueta_pdf_url, msds_pdf_url, notas_agronomicas,
  nombre_app, editado_por, editado_at,
  nombre_boleta, justificacion,
  departamento, departamento_id,
  ingrediente_activo, ingrediente_activo_id,
  modalidad_aplicacion_id, modalidad_aplicacion_cod,
  pertenece_aplicaciones
`;

export function CatalogoProductosPage() {
  const empresaId = useEmpresaId();
  const [loading, setLoading] = useState(false);
  const [productos, setProductos] = useState<Producto[]>([]);
  const [lineas, setLineas] = useState<Linea[]>([]);
  const [ingredientesActivos, setIngredientesActivos] = useState<Array<{ id: number; codigo: string; nombre: string }>>([]);
  const [departamentos, setDepartamentos] = useState<Array<{ id: number; codigo: string; nombre: string }>>([]);
  const [unidades, setUnidades] = useState<Array<{ id: number; codigo: string; nombre: string }>>([]);
  const [modalidades, setModalidades] = useState<Array<{ id: number; codigo: string; nombre: string; requiere_equipo: boolean; requiere_agua: boolean }>>([]);
  const [filtroLinea, setFiltroLinea] = useState<string>('');
  const [filtroActivo, setFiltroActivo] = useState<'todos' | 'activos' | 'inactivos'>('activos');
  const [filtroPertenece, setFiltroPertenece] = useState<'todos' | 'aplicaciones' | 'otros'>('todos');
  const [busqueda, setBusqueda] = useState('');
  const [busquedaInput, setBusquedaInput] = useState('');
  const [pagina, setPagina] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [edicion, setEdicion] = useState<Producto | null>(null);
  const [traza, setTraza] = useState<Producto | null>(null);
  const [msg, setMsg] = useState<{ tipo: 'ok' | 'err'; texto: string } | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const sort = useTableSort<ProductoSortKey>({ key: 'codigo', direction: 'asc' });

  function showMsg(tipo: 'ok' | 'err', texto: string) {
    setMsg({ tipo, texto });
    setTimeout(() => setMsg(null), 4000);
  }

  const cargar = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Sesion vencida. Inicie sesion nuevamente para cargar el catalogo.');
      }
      const params = new URLSearchParams();
      if (filtroLinea && filtroLinea !== LINEA_SIN_ASIGNAR) params.set('linea', filtroLinea);
      if (busqueda.trim()) params.set('q', busqueda.trim());
      if (filtroActivo === 'activos') params.set('activo', 'true');
      else if (filtroActivo === 'inactivos') params.set('activo', 'false');
      if (filtroPertenece === 'aplicaciones') params.set('pertenece_aplicaciones', 'true');
      else if (filtroPertenece === 'otros') params.set('pertenece_aplicaciones', 'false');

      const hdrs = {
        'x-empresa-id': String(empresaId),
        ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
      };
      // Promise.allSettled: si algún endpoint falla, los otros siguen funcionando
      const [respProd, respLin, respGQ, respDep, respUM, respMod] = await Promise.allSettled([
        fetch(buildApiUrl(`/aplicaciones/catalogo/productos?${params.toString()}`), { headers: hdrs }),
        fetch(buildApiUrl('/aplicaciones/catalogo/lineas'), { headers: hdrs }),
        fetch(buildApiUrl('/aplicaciones/catalogo/ingredientes-activos'), { headers: hdrs }),
        fetch(buildApiUrl('/aplicaciones/catalogo/departamentos'), { headers: hdrs }),
        fetch(buildApiUrl('/aplicaciones/catalogo/unidades-medida'), { headers: hdrs }),
        fetch(buildApiUrl('/aplicaciones/catalogo/modalidades-aplicacion'), { headers: hdrs }),
      ]);
      const safeJson = async (r: any) => {
        if (r.status !== 'fulfilled') return {};
        try { return await r.value.json(); } catch { return {}; }
      };
      const jp = await safeJson(respProd);
      const jl = await safeJson(respLin);
      const jgq = await safeJson(respGQ);
      const jdp = await safeJson(respDep);
      const jum = await safeJson(respUM);
      const jmod = await safeJson(respMod);

      if (respProd.status === 'rejected') {
        throw new Error('No se pudo conectar con el backend de Aplicaciones.');
      }
      if (respProd.status === 'fulfilled' && !(respProd.value as Response).ok) {
        const status = (respProd.value as Response).status;
        if (status === 401) throw new Error('Sesion vencida. Inicie sesion nuevamente para cargar el catalogo.');
        if (status === 403) throw new Error('Su usuario no tiene permiso para ver el catalogo de productos.');
        throw new Error(jp?.error || 'No se pudo cargar el catalogo');
      }

      const ingredientes = Array.isArray(jgq.ingredientes) ? jgq.ingredientes : (Array.isArray(jgq.grupos) ? jgq.grupos : []);
      const deps = Array.isArray(jdp.departamentos) ? jdp.departamentos : [];
      const mods = Array.isArray(jmod.modalidades) ? jmod.modalidades : [];

      let productosApi = Array.isArray(jp.productos) ? jp.productos : [];
      let lineasApi = Array.isArray(jl.lineas) ? jl.lineas : [];

      if (productosApi.length === 0) {
        const { data: bodegaFinca } = await supabase
          .from('inv_bodegas')
          .select('id')
          .eq('empresa_id', empresaId)
          .ilike('nombre', 'Finca')
          .maybeSingle();
        let qProd = supabase
          .from('inv_productos')
          .select(PRODUCTOS_SELECT)
          .eq('empresa_id', empresaId)
          .eq('bodega_id', Number((bodegaFinca as any)?.id || 0))
          .order('codigo');
        if (filtroLinea && filtroLinea !== LINEA_SIN_ASIGNAR) qProd = qProd.eq('vfp_linea_id', filtroLinea);
        if (filtroActivo === 'activos') qProd = qProd.eq('activo', true);
        else if (filtroActivo === 'inactivos') qProd = qProd.eq('activo', false);
        if (filtroPertenece === 'aplicaciones') qProd = qProd.eq('pertenece_aplicaciones', true);
        else if (filtroPertenece === 'otros') qProd = qProd.eq('pertenece_aplicaciones', false);
        if (busqueda.trim()) {
          const term = busqueda.trim();
          qProd = qProd.or(`descripcion.ilike.%${term}%,nombre_app.ilike.%${term}%,codigo.ilike.%${term}%`);
        }
        const { data: fallbackProductos } = await qProd;
        productosApi = Array.isArray(fallbackProductos) ? fallbackProductos as Producto[] : productosApi;
      }

      if (lineasApi.length === 0) {
        const { data: bodegaFinca } = await supabase
          .from('inv_bodegas')
          .select('id')
          .eq('empresa_id', empresaId)
          .ilike('nombre', 'Finca')
          .maybeSingle();
        const { data: fallbackLineas } = await supabase
          .from('inv_productos')
          .select('vfp_linea_id, vfp_linea_nombre')
          .eq('empresa_id', empresaId)
          .eq('bodega_id', Number((bodegaFinca as any)?.id || 0))
          .not('vfp_linea_id', 'is', null);
        const map = new Map<string, string>();
        (fallbackLineas || []).forEach((p: any) => {
          if (p.vfp_linea_id && !map.has(p.vfp_linea_id)) map.set(p.vfp_linea_id, p.vfp_linea_nombre || p.vfp_linea_id);
        });
        lineasApi = Array.from(map.entries())
          .map(([id, nombre]) => ({ id, nombre }))
          .sort((a, b) => (a.nombre || '').localeCompare(b.nombre || '', 'es'));
      }

      setProductos(productosApi);
      setLineas(lineasApi);
      setIngredientesActivos(ingredientes);
      setDepartamentos(deps);
      setUnidades(Array.isArray(jum.unidades) ? jum.unidades : []);
      setModalidades(mods);
    } catch (e: any) {
      const texto = e?.message || 'Error al cargar';
      setProductos(prev => prev.length > 0 ? prev : []);
      setLoadError(texto);
      showMsg('err', texto);
    } finally {
      setLoading(false);
    }
  }, [empresaId, filtroLinea, busqueda, filtroActivo, filtroPertenece]);

  useEffect(() => {
    const timer = window.setTimeout(() => setBusqueda(busquedaInput.trim()), 350);
    return () => window.clearTimeout(timer);
  }, [busquedaInput]);

  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => { setPagina(1); }, [busqueda, filtroLinea, filtroActivo, filtroPertenece, sort.value.key, sort.value.direction, pageSize]);

  const productosSinLinea = useMemo(
    () => productos.filter(p => !String(p.vfp_linea_id || '').trim() && !String(p.vfp_linea_nombre || '').trim()).length,
    [productos],
  );
  const productosFiltrados = useMemo(() => {
    const base = filtroLinea === LINEA_SIN_ASIGNAR
      ? productos.filter(p => !String(p.vfp_linea_id || '').trim() && !String(p.vfp_linea_nombre || '').trim())
      : productos;
    return sortRows(base, sort.value, (p, key) => {
      switch (key) {
        case 'codigo': return p.codigo;
        case 'descripcion': return p.nombre_app || p.descripcion;
        case 'linea': return p.departamento || p.vfp_linea_nombre;
        case 'medida': return p.unidad_medida;
        case 'stock': return Number(p.stock_actual ?? 0);
        case 'costo': return Number(p.costo_promedio ?? 0);
        case 'moneda': return p.moneda_costo || 'CRC';
        case 'reingreso': return p.reingreso;
        case 'banda': return p.banda_toxicologica || p.banda_color;
        case 'mag': return p.registro_mag;
        default: return '';
      }
    });
  }, [productos, filtroLinea, sort.value]);
  const cargandoInicial = loading && productos.length === 0;
  const refrescando = loading && productos.length > 0;
  const sinProductosPorEmpresa = !loading && productosFiltrados.length === 0 && !busquedaInput.trim() && !filtroLinea;
  const totalPaginas = Math.max(1, Math.ceil(productosFiltrados.length / pageSize));
  const paginaActual = Math.min(pagina, totalPaginas);
  const inicio = productosFiltrados.length === 0 ? 0 : (paginaActual - 1) * pageSize + 1;
  const fin = Math.min(productosFiltrados.length, paginaActual * pageSize);
  const productosPagina = useMemo(
    () => productosFiltrados.slice((paginaActual - 1) * pageSize, paginaActual * pageSize),
    [productosFiltrados, paginaActual, pageSize],
  );

  // Códigos con al menos una línea en boletas — para atenuar "Trazabilidad"
  // cuando el producto nunca se usó (evita abrir el modal para ver "sin resultados").
  const [articulosConTraza, setArticulosConTraza] = useState<Set<string>>(new Set());
  useEffect(() => {
    const codigos = [...new Set(productosPagina.map(p => p.codigo).filter(Boolean))];
    if (codigos.length === 0) { setArticulosConTraza(new Set()); return; }
    let cancelado = false;
    (async () => {
      const { data } = await supabase
        .from('app_boleta_productos')
        .select('articulo')
        .eq('empresa_id', empresaId)
        .eq('es_agua', false)
        .in('articulo', codigos);
      if (cancelado) return;
      setArticulosConTraza(new Set((data ?? []).map((r: any) => r.articulo as string)));
    })();
    return () => { cancelado = true; };
  }, [productosPagina, empresaId]);

  return (
    <div style={{ padding: '20px 24px', minHeight: '100%', background: 'var(--surface-base)', color: 'var(--ink)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
        <Boxes size={22} style={{ color: 'var(--accent-main)' }} />
        <h1 style={{ fontSize: '20px', fontWeight: 800, margin: 0 }}>Catálogo de Productos</h1>
        <span style={{ fontSize: '12px', color: 'var(--ink-muted)' }}>
          {`${productosFiltrados.length} producto${productosFiltrados.length !== 1 ? 's' : ''}`}
        </span>
        {refrescando && (
          <span style={{ fontSize: '11px', color: 'var(--ink-muted)', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Loader2 size={12} className="animate-spin" /> Actualizando
          </span>
        )}
        {!cargandoInicial && productosSinLinea > 0 && (
          <button
            type="button"
            onClick={() => setFiltroLinea(LINEA_SIN_ASIGNAR)}
            title="Ver productos sin linea"
            style={{
              fontSize: '11px',
              color: '#fbbf24',
              background: 'rgba(245,158,11,0.12)',
              border: '1px solid rgba(245,158,11,0.35)',
              borderRadius: '999px',
              padding: '4px 8px',
              fontWeight: 800,
              cursor: 'pointer',
            }}
          >
            Sin linea: {productosSinLinea}
          </button>
        )}
        <span style={{
          fontSize: '11px',
          color: 'var(--accent-main)',
          background: 'var(--accent-soft, rgba(37,99,235,.14))',
          border: '1px solid var(--line)',
          borderRadius: '999px',
          padding: '4px 8px',
          fontWeight: 700,
        }}>
          Empresa activa: {empresaId}
        </span>
        <button type="button" onClick={() => setEdicion(productoNuevo())} style={{
          marginLeft: 'auto', padding: '8px 13px', borderRadius: '8px', border: '1px solid rgba(34,197,94,.45)',
          background: 'rgba(34,197,94,.12)', color: '#4ade80', fontSize: '12px', fontWeight: 800,
          display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer',
        }}><Plus size={14} /> Nuevo producto</button>
      </div>

      {/* Filtros */}
      <div style={{
        display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '10px', marginBottom: '14px',
        padding: '12px', borderRadius: '10px', background: 'var(--surface-raised)', border: '1px solid var(--line)',
      }}>
        <div style={{ position: 'relative' }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: 11, color: 'var(--ink-faint)' }} />
          <input
            value={busquedaInput}
            onChange={(e) => setBusquedaInput(e.target.value)}
            placeholder="Buscar por código, descripción o nombre…"
            style={{
              width: '100%', padding: '8px 36px 8px 32px', borderRadius: '8px',
              border: '1px solid var(--line)', background: 'var(--surface-base)', color: 'var(--ink)',
              fontSize: '13px', outline: 'none',
            }}
          />
          {busquedaInput.trim() && (
            <button
              type="button"
              onClick={() => {
                setBusquedaInput('');
                setBusqueda('');
              }}
              title="Limpiar busqueda"
              style={{
                position: 'absolute',
                right: 8,
                top: 7,
                width: 22,
                height: 22,
                borderRadius: '6px',
                border: '1px solid var(--line)',
                background: 'rgba(255,255,255,0.04)',
                color: 'var(--ink-muted)',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
              }}
            >
              <X size={13} />
            </button>
          )}
        </div>
        <select
          value={filtroLinea}
          onChange={(e) => setFiltroLinea(e.target.value)}
          style={{
            padding: '8px 10px', borderRadius: '8px', border: '1px solid var(--line)',
            background: 'var(--surface-base)', color: 'var(--ink)', fontSize: '13px',
            minWidth: '180px',
          }}
        >
          <option value="">Todas las líneas</option>
          <option value={LINEA_SIN_ASIGNAR}>Sin linea ({productosSinLinea})</option>
          {lineas.map(l => <option key={l.id} value={l.id}>{l.nombre}</option>)}
        </select>
        <select
          value={filtroActivo}
          onChange={(e) => setFiltroActivo(e.target.value as any)}
          style={{
            padding: '8px 10px', borderRadius: '8px', border: '1px solid var(--line)',
            background: 'var(--surface-base)', color: 'var(--ink)', fontSize: '13px',
          }}
        >
          <option value="activos">Activos</option>
          <option value="inactivos">Inactivos</option>
          <option value="todos">Todos</option>
        </select>
        <select
          value={filtroPertenece}
          onChange={(e) => setFiltroPertenece(e.target.value as any)}
          title="Filtra si el producto es un agroquímico/insumo real de Aplicaciones, o solo aparece en el catálogo compartido"
          style={{
            padding: '8px 10px', borderRadius: '8px', border: '1px solid var(--line)',
            background: 'var(--surface-base)', color: 'var(--ink)', fontSize: '13px',
          }}
        >
          <option value="todos">Todos (Aplicaciones y otros)</option>
          <option value="aplicaciones">Solo Aplicaciones</option>
          <option value="otros">Solo no-Aplicaciones</option>
        </select>
      </div>

      {/* Tabla */}
      <div style={{ borderRadius: '10px', background: 'var(--surface-raised)', border: '1px solid var(--line)', overflow: 'hidden' }}>
        {cargandoInicial ? (
          <div style={{ padding: '40px', textAlign: 'center', color: 'var(--ink-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
            <Loader2 size={16} className="animate-spin" /> Cargando catálogo…
          </div>
        ) : productosFiltrados.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: 'var(--ink-muted)' }}>
            <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--ink)' }}>
              Sin resultados
            </div>
            <div style={{ marginTop: 8, fontSize: '12px' }}>
              {loadError
                ? loadError
                : sinProductosPorEmpresa
                  ? `No hay productos cargados para la empresa activa ${empresaId}. Revise la empresa seleccionada al iniciar sesion.`
                  : 'Cambie los filtros o limpie la busqueda para ver mas productos.'}
            </div>
          </div>
        ) : (
          <>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: 'rgba(255,255,255,0.03)' }}>
                <th style={thStyle} title="Activo"></th>
                <th style={thStyle}><SortableTh sort={sort} sortKey="codigo" align="center">Codigo</SortableTh></th>
                <th style={{ ...thStyle, textAlign: 'left' }}><SortableTh sort={sort} sortKey="descripcion">Descripcion</SortableTh></th>
                <th style={thStyle}><SortableTh sort={sort} sortKey="linea" align="center">Linea</SortableTh></th>
                <th style={thStyle}><SortableTh sort={sort} sortKey="medida" align="center">Medida</SortableTh></th>
                <th style={{ ...thStyle, textAlign: 'right' }}><SortableTh sort={sort} sortKey="stock" align="right">Stock</SortableTh></th>
                <th style={thStyle}><SortableTh sort={sort} sortKey="reingreso" align="center">Reingreso</SortableTh></th>
                <th style={{ ...thStyle, textAlign: 'right' }}><SortableTh sort={sort} sortKey="costo" align="right">Costo</SortableTh></th>
                <th style={thStyle}><SortableTh sort={sort} sortKey="moneda" align="center">Moneda</SortableTh></th>
                <th style={thStyle}><SortableTh sort={sort} sortKey="banda" align="center">Banda</SortableTh></th>
                <th style={thStyle}><SortableTh sort={sort} sortKey="mag" align="center">MAG</SortableTh></th>
                <th style={thStyle}></th>
              </tr>
            </thead>
            <tbody>
              {productosPagina.map(p => (
                <tr key={p.id} style={{ borderTop: '1px solid var(--line-dim)' }}>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    <span title={p.activo ? 'Activo' : 'Inactivo'} style={{
                      display: 'inline-block', width: '9px', height: '9px', borderRadius: '999px',
                      background: p.activo ? '#22c55e' : 'var(--ink-faint)',
                    }} />
                  </td>
                  <td style={tdMonoStyle}>{p.codigo}</td>
                  <td style={tdStyle}>
                    <div style={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: '6px' }}>
                      {p.nombre_app || p.descripcion}
                      {!p.pertenece_aplicaciones && (
                        <span title="No es un insumo de Aplicaciones (empaque, otra área)" style={{
                          fontSize: '9px', fontWeight: 800, color: 'var(--ink-muted)',
                          background: 'rgba(148,163,184,0.15)', border: '1px solid rgba(148,163,184,0.3)',
                          borderRadius: '999px', padding: '1px 6px', textTransform: 'uppercase', letterSpacing: '0.04em',
                        }}>No Aplicaciones</span>
                      )}
                    </div>
                    {(p.ingrediente_activo || p.vfp_grupo_quimico_nom) && (
                      <div style={{ fontSize: '11px', color: 'var(--ink-muted)' }}>{p.ingrediente_activo || p.vfp_grupo_quimico_nom}</div>
                    )}
                    {p.justificacion && (
                      <div style={{ fontSize: '10px', color: '#c4b5fd', marginTop: '2px', fontStyle: 'italic' }}>{p.justificacion}</div>
                    )}
                  </td>
                  <td style={{ ...tdStyle, fontSize: '11px', color: 'var(--ink-muted)' }}>
                    {p.departamento || p.vfp_linea_nombre || (
                      <span style={{
                        color: '#fbbf24',
                        background: 'rgba(245,158,11,0.12)',
                        border: '1px solid rgba(245,158,11,0.30)',
                        borderRadius: '999px',
                        padding: '2px 7px',
                        fontWeight: 800,
                      }}>
                        Sin linea
                      </span>
                    )}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>{p.unidad_medida || '—'}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'monospace', color: (Number(p.stock_actual) || 0) < 0 ? '#ef4444' : 'var(--ink)' }}>
                    {fmtN(p.stock_actual, 2)}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center', fontSize: '11px' }}>{p.reingreso || '—'}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'monospace', color: 'var(--ink-muted)' }}>
                    {fmtMoney(p.costo_promedio, p.moneda_costo)}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    <span style={{
                      padding: '2px 7px',
                      borderRadius: '999px',
                      fontSize: '10px',
                      fontWeight: 800,
                      background: p.moneda_costo === 'USD' ? 'rgba(34,197,94,0.12)' : 'rgba(59,130,246,0.12)',
                      color: p.moneda_costo === 'USD' ? '#86efac' : '#93c5fd',
                      border: `1px solid ${p.moneda_costo === 'USD' ? 'rgba(34,197,94,0.35)' : 'rgba(59,130,246,0.35)'}`,
                    }}>
                      {p.moneda_costo || 'CRC'}
                    </span>
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    {p.banda_toxicologica ? (
                      <span style={{
                        padding: '2px 8px', borderRadius: '999px', fontSize: '10px', fontWeight: 800,
                        background: bandaBg(p.banda_color), color: bandaFg(p.banda_color),
                      }}>{p.banda_toxicologica}</span>
                    ) : '—'}
                  </td>
                  <td style={{ ...tdStyle, fontSize: '11px', color: 'var(--ink-muted)' }}>{p.registro_mag || '—'}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {(() => {
                      const tieneTraza = articulosConTraza.has(p.codigo);
                      return (
                        <button
                          onClick={() => tieneTraza && setTraza(p)}
                          disabled={!tieneTraza}
                          title={tieneTraza ? 'Trazabilidad por boletas' : 'Sin movimientos en boletas todavía'}
                          style={{
                            padding: '5px 9px', borderRadius: '6px', fontSize: '12px', fontWeight: 700,
                            border: `1px solid ${tieneTraza ? 'rgba(59,130,246,0.4)' : 'var(--line)'}`,
                            background: tieneTraza ? 'rgba(37,99,235,0.10)' : 'transparent',
                            color: tieneTraza ? '#93c5fd' : 'var(--ink-faint)',
                            cursor: tieneTraza ? 'pointer' : 'not-allowed',
                            opacity: tieneTraza ? 1 : 0.5,
                            display: 'inline-flex', alignItems: 'center', gap: '4px',
                            marginRight: '6px',
                          }}>
                          <ListTree size={12} /> Trazabilidad
                        </button>
                      );
                    })()}
                    <button
                      onClick={() => setEdicion(p)}
                      title="Editar"
                      style={{
                        padding: '5px 9px', borderRadius: '6px', fontSize: '12px', fontWeight: 700,
                        border: '1px solid rgba(167,139,250,0.4)', background: 'rgba(124,58,237,0.10)',
                        color: '#a78bfa', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px',
                      }}>
                      <Pencil size={12} /> Editar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            justifyContent: 'space-between',
            padding: '10px 12px',
            borderTop: '1px solid var(--line)',
            background: 'rgba(255,255,255,0.02)',
            color: 'var(--ink-muted)',
            fontSize: '12px',
          }}>
            <div>
              Mostrando {inicio}-{fin} de {productosFiltrados.length}
            </div>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                style={{
                  padding: '6px 8px',
                  borderRadius: '7px',
                  border: '1px solid var(--line)',
                  background: 'var(--surface-base)',
                  color: 'var(--ink)',
                  fontSize: '12px',
                }}
              >
                <option value={25}>25 filas</option>
                <option value={50}>50 filas</option>
                <option value={100}>100 filas</option>
              </select>
              <button
                type="button"
                onClick={() => setPagina(p => Math.max(1, p - 1))}
                disabled={paginaActual <= 1}
                title="Pagina anterior"
                style={pageButtonStyle(paginaActual <= 1)}
              >
                <ChevronLeft size={14} />
              </button>
              <span style={{ minWidth: 86, textAlign: 'center' }}>
                Pagina {paginaActual} / {totalPaginas}
              </span>
              <button
                type="button"
                onClick={() => setPagina(p => Math.min(totalPaginas, p + 1))}
                disabled={paginaActual >= totalPaginas}
                title="Pagina siguiente"
                style={pageButtonStyle(paginaActual >= totalPaginas)}
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
          </>
        )}
      </div>

      {/* Toast */}
      {msg && createPortal(
        <div style={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 10000,
          padding: '12px 18px', borderRadius: '10px', fontWeight: 700, fontSize: '13px',
          border: '1px solid', boxShadow: '0 6px 20px rgba(0,0,0,0.4)',
          background: msg.tipo === 'ok' ? 'rgba(22,163,74,0.12)' : 'rgba(239,68,68,0.12)',
          borderColor: msg.tipo === 'ok' ? '#22c55e' : '#ef4444',
          color: msg.tipo === 'ok' ? '#4ade80' : '#fca5a5',
        }}>{msg.texto}</div>,
        document.body
      )}

      {/* Modal edición */}
      {edicion && (
        <ModalEdicion
          producto={edicion}
          empresaId={empresaId}
          ingredientesActivos={ingredientesActivos}
          departamentos={departamentos}
          unidades={unidades}
          modalidades={modalidades}
          onClose={() => setEdicion(null)}
          onSaved={(actualizado, propagado) => {
            setProductos(prev => edicion.id === 0 ? [...prev, actualizado] : prev.map(p => p.id === actualizado.id ? actualizado : p));
            setEdicion(null);
            const partes = [edicion.id === 0 ? 'Producto creado' : 'Producto actualizado'];
            if (propagado && (propagado.plan > 0 || propagado.boletas > 0)) {
              const sub = [];
              if (propagado.plan > 0) sub.push(`${propagado.plan} filas del plan`);
              if (propagado.boletas > 0) sub.push(`${propagado.boletas} líneas de boleta`);
              partes.push(`· Propagado a ${sub.join(' y ')}`);
            }
            showMsg('ok', partes.join(' '));
          }}
          onError={(m) => showMsg('err', m)}
        />
      )}

      {/* Modal trazabilidad */}
      {traza && (
        <ModalTrazabilidad
          producto={traza}
          empresaId={empresaId}
          onClose={() => setTraza(null)}
        />
      )}
    </div>
  );
}

// ─── Estilos comunes ─────────────────────────────────────────────────
const thStyle: React.CSSProperties = {
  padding: '10px 12px', textAlign: 'center', fontSize: '11px', fontWeight: 700,
  textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--ink-muted)',
};
const tdStyle: React.CSSProperties = {
  padding: '10px 12px', verticalAlign: 'middle',
};
const tdMonoStyle: React.CSSProperties = {
  ...tdStyle, fontFamily: 'monospace', fontSize: '12px', textAlign: 'center', color: '#93c5fd',
};

function pageButtonStyle(disabled: boolean): React.CSSProperties {
  return {
    width: 30,
    height: 30,
    borderRadius: '7px',
    border: '1px solid var(--line)',
    background: disabled ? 'rgba(255,255,255,0.02)' : 'rgba(59,130,246,0.10)',
    color: disabled ? 'var(--ink-faint)' : 'var(--ink)',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: disabled ? 'not-allowed' : 'pointer',
  };
}

function bandaBg(color: string | null): string {
  switch ((color || '').toLowerCase()) {
    case 'rojo': return 'rgba(239,68,68,0.18)';
    case 'amarillo': return 'rgba(245,158,11,0.18)';
    case 'azul': return 'rgba(59,130,246,0.18)';
    case 'verde': return 'rgba(34,197,94,0.18)';
    default: return 'rgba(148,163,184,0.18)';
  }
}
function bandaFg(color: string | null): string {
  switch ((color || '').toLowerCase()) {
    case 'rojo': return '#fca5a5';
    case 'amarillo': return '#fbbf24';
    case 'azul': return '#93c5fd';
    case 'verde': return '#4ade80';
    default: return '#cbd5e1';
  }
}

// ════════════════════════════════════════════════════════════════════
// Modal de edición — tabs: General | Operativo | G.A.P. | Seguridad | Historial
// ════════════════════════════════════════════════════════════════════

interface ModalProps {
  producto: Producto;
  empresaId: number;
  ingredientesActivos: Array<{ id: number; codigo: string; nombre: string }>;
  departamentos: Array<{ id: number; codigo: string; nombre: string }>;
  unidades: Array<{ id: number; codigo: string; nombre: string }>;
  modalidades: Array<{ id: number; codigo: string; nombre: string; requiere_equipo: boolean; requiere_agua: boolean }>;
  onClose: () => void;
  onSaved: (p: Producto, propagado?: { plan: number; boletas: number }) => void;
  onError: (m: string) => void;
}

function ModalEdicion({ producto, empresaId, ingredientesActivos, departamentos, unidades, modalidades, onClose, onSaved, onError }: ModalProps) {
  const esNuevo = producto.id === 0;
  const [tab, setTab] = useState<'general' | 'operativo' | 'gap' | 'seguridad' | 'historial'>('general');
  const [form, setForm] = useState<Producto>(producto);
  const [guardando, setGuardando] = useState(false);
  const [historial, setHistorial] = useState<AuditFila[]>([]);
  const [unidadTexto, setUnidadTexto] = useState(producto.unidad_medida ?? '');
  const [departamentoTexto, setDepartamentoTexto] = useState(
    departamentos.find(d => d.id === producto.departamento_id)?.nombre ?? producto.departamento ?? ''
  );
  const [ingredienteTexto, setIngredienteTexto] = useState(
    ingredientesActivos.find(i => i.id === producto.ingrediente_activo_id)?.nombre ?? producto.ingrediente_activo ?? ''
  );

  useEffect(() => {
    const cerrarConEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !guardando) onClose();
    };
    document.addEventListener('keydown', cerrarConEscape);
    const overflowAnterior = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', cerrarConEscape);
      document.body.style.overflow = overflowAnterior;
    };
  }, [guardando, onClose]);

  // Cargar historial cuando se abre el tab
  useEffect(() => {
    if (tab !== 'historial' || esNuevo) return;
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const resp = await fetch(buildApiUrl(`/aplicaciones/catalogo/productos/${producto.id}`), {
          headers: {
            'x-empresa-id': String(empresaId),
            ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
          },
        });
        const j = await resp.json().catch(() => ({}));
        if (resp.ok) setHistorial(j.historial || []);
      } catch { /* */ }
    })();
  }, [tab, producto.id, empresaId, esNuevo]);

  function setField<K extends keyof Producto>(k: K, v: Producto[K]) {
    setForm(prev => ({ ...prev, [k]: v }));
  }

  function toggleEPI(item: string) {
    const actual = form.epi_requerido || [];
    const next = actual.includes(item) ? actual.filter(x => x !== item) : [...actual, item];
    setField('epi_requerido', next);
  }

  async function guardar() {
    if (esNuevo && !form.codigo.trim()) return onError('El código es obligatorio');
    if (esNuevo && !form.descripcion.trim()) return onError('La descripción es obligatoria');
    if (esNuevo && !unidadTexto.trim()) return onError('Seleccione o escriba la unidad de medida');
    setGuardando(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const normalizar = (v: string) => v.trim().toLocaleUpperCase('es');
      let unidadMedida = unidadTexto.trim() || null;
      if (unidadMedida) {
        const existente = unidades.find(u => normalizar(u.codigo) === normalizar(unidadMedida!) || normalizar(u.nombre) === normalizar(unidadMedida!));
        if (existente) unidadMedida = existente.codigo;
        else {
          const { data: creada, error } = await supabase.from('app_unidades_medida')
            .insert({ codigo: unidadMedida.slice(0, 20), nombre: unidadMedida, activo: true })
            .select('codigo').single();
          if (error) throw new Error(`No se pudo crear la unidad: ${error.message}`);
          unidadMedida = creada.codigo;
        }
      }
      let departamentoId = form.departamento_id;
      if (departamentoTexto.trim()) {
        const existente = departamentos.find(d => normalizar(d.nombre) === normalizar(departamentoTexto) || normalizar(d.codigo) === normalizar(departamentoTexto));
        if (existente) departamentoId = existente.id;
        else {
          const { data: creado, error } = await supabase.from('app_departamentos')
            .insert({ empresa_id: empresaId, codigo: `USR-${Date.now()}`, nombre: departamentoTexto.trim().toUpperCase(), activo: true })
            .select('id').single();
          if (error) throw new Error(`No se pudo crear el departamento: ${error.message}`);
          departamentoId = creado.id;
        }
      } else departamentoId = null;
      let ingredienteId = form.ingrediente_activo_id;
      if (ingredienteTexto.trim()) {
        const existente = ingredientesActivos.find(i => normalizar(i.nombre) === normalizar(ingredienteTexto) || normalizar(i.codigo) === normalizar(ingredienteTexto));
        if (existente) ingredienteId = existente.id;
        else {
          const { data: creado, error } = await supabase.from('app_ingredientes_activos')
            .insert({ empresa_id: empresaId, codigo: `USR-${Date.now()}`, nombre: ingredienteTexto.trim().toUpperCase(), activo: true })
            .select('id').single();
          if (error) throw new Error(`No se pudo crear la composición: ${error.message}`);
          ingredienteId = creado.id;
        }
      } else ingredienteId = null;
      // Solo enviar campos editables
      const payload: any = {
        ...(esNuevo ? { codigo: form.codigo.trim(), descripcion: form.descripcion.trim() } : {}),
        nombre_app: form.nombre_app,
        nombre_boleta: form.nombre_boleta,
        descripcion_detallada: form.descripcion_detallada,
        unidad_medida: unidadMedida,
        moneda_costo: form.moneda_costo || 'CRC',
        stock_minimo: form.stock_minimo,
        observaciones: form.observaciones,
        latencia_dias: form.latencia_dias,
        reingreso: form.reingreso,
        // Clasificación agronómica (FKs a catálogos normalizados — el backend
        // sincroniza también los campos TEXT y propaga a plan + boletas)
        justificacion: form.justificacion,
        ingrediente_activo_id: ingredienteId,
        departamento_id: departamentoId,
        modalidad_aplicacion_id: form.modalidad_aplicacion_id,
        // G.A.P.
        registro_mag: form.registro_mag,
        vencimiento_etiqueta: form.vencimiento_etiqueta,
        banda_toxicologica: form.banda_toxicologica,
        banda_color: form.banda_color,
        // Seguridad
        epi_requerido: form.epi_requerido,
        restricciones_clima: form.restricciones_clima,
        zona_buffer_m: form.zona_buffer_m,
        requiere_autorizacion: form.requiere_autorizacion,
        // Archivos + notas
        etiqueta_pdf_url: form.etiqueta_pdf_url,
        msds_pdf_url: form.msds_pdf_url,
        notas_agronomicas: form.notas_agronomicas,
        activo: form.activo,
        pertenece_aplicaciones: form.pertenece_aplicaciones,
      };
      const resp = await fetch(buildApiUrl(esNuevo ? '/aplicaciones/catalogo/productos' : `/aplicaciones/catalogo/productos/${producto.id}`), {
        method: esNuevo ? 'POST' : 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-empresa-id': String(empresaId),
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
        body: JSON.stringify(payload),
      });
      const j = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(j?.error || `HTTP ${resp.status}`);
      onSaved(j.producto, { plan: Number(j.propagado_plan || 0), boletas: Number(j.propagado_boletas || 0) });
    } catch (e: any) {
      onError(e?.message || 'No se pudo guardar');
    } finally {
      setGuardando(false);
    }
  }

  return createPortal(
    <div className="producto-modal-backdrop" onMouseDown={event => {
      if (event.target === event.currentTarget && !guardando) onClose();
    }} style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(2,6,23,0.78)', backdropFilter: 'blur(5px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
    }}>
      <div role="dialog" aria-modal="true" aria-labelledby="producto-modal-titulo" className="producto-modal" style={{
        width: '100%', maxWidth: '900px', maxHeight: '92vh', borderRadius: '18px',
        background: 'var(--surface-base)', border: '1px solid var(--line)', overflow: 'hidden',
        boxShadow: '0 28px 80px rgba(0,0,0,0.62)', display: 'flex', flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--line)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', background: 'var(--surface-raised)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '13px', minWidth: 0 }}>
            <div style={{ width: '42px', height: '42px', borderRadius: '12px', display: 'grid', placeItems: 'center', background: 'var(--emp-accent-bg)', border: '1px solid var(--emp-accent)', color: 'var(--emp-accent-txt)', flexShrink: 0 }}>
              <Boxes size={21} />
            </div>
            <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: '11px', color: 'var(--emp-accent-txt)', fontWeight: 800, marginBottom: '4px', letterSpacing: '0.08em', textTransform: 'uppercase' }}>{esNuevo ? 'Nuevo producto' : `Producto ERP · Código ${producto.codigo}`}</div>
            <h2 id="producto-modal-titulo" style={{ fontSize: '19px', lineHeight: 1.25, color: 'var(--ink)', fontWeight: 800, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{esNuevo ? 'Crear producto' : (form.nombre_app || form.descripcion)}</h2>
            {producto.editado_at && (
              <div style={{ fontSize: '11px', color: 'var(--ink-muted)', marginTop: '5px' }}>
                Última edición: {new Date(producto.editado_at).toLocaleString('es-CR')}
              </div>
            )}
            </div>
          </div>
          <button aria-label="Cerrar" title="Cerrar (Esc)" onClick={onClose} disabled={guardando} style={{ width: '38px', height: '38px', borderRadius: '10px', display: 'grid', placeItems: 'center', background: 'var(--surface-overlay)', border: '1px solid var(--line)', color: 'var(--ink-muted)', cursor: guardando ? 'not-allowed' : 'pointer', flexShrink: 0 }}>
            <X size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div className="producto-modal-tabs" role="tablist" style={{ display: 'flex', borderBottom: '1px solid var(--line)', padding: '8px 16px', gap: '6px', flexShrink: 0, background: 'var(--surface-raised)', overflowX: 'auto' }}>
          {[
            { id: 'general', label: 'General' },
            { id: 'operativo', label: 'Operativo' },
            { id: 'gap', label: 'G.A.P.' },
            { id: 'seguridad', label: 'Seguridad' },
            ...(!esNuevo ? [{ id: 'historial', label: 'Historial' }] : []),
          ].map(t => (
            <button
              key={t.id}
              role="tab"
              aria-selected={tab === t.id}
              onClick={() => setTab(t.id as any)}
              style={{
                padding: '9px 14px', borderRadius: '9px', fontSize: '12px', fontWeight: 800,
                border: `1px solid ${tab === t.id ? 'var(--emp-accent)' : 'transparent'}`,
                background: tab === t.id ? 'var(--emp-accent-bg)' : 'transparent',
                cursor: 'pointer', color: tab === t.id ? 'var(--emp-accent-txt)' : 'var(--ink-muted)', whiteSpace: 'nowrap',
              }}>{t.label}</button>
          ))}
        </div>

        {/* Contenido */}
        <div className="producto-modal-content" style={{ padding: '22px 24px 26px', overflowY: 'auto', flex: 1, background: 'var(--surface-base)', color: 'var(--ink)' }}>
          {tab === 'general' && (
            <div className="producto-modal-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px 18px' }}>
              {esNuevo && <>
                <Field label="Código *"><input value={form.codigo} onChange={e => setField('codigo', e.target.value.toUpperCase())} maxLength={40} autoFocus style={inputStyle} /></Field>
                <Field label="Descripción base *"><input value={form.descripcion} onChange={e => setField('descripcion', e.target.value)} maxLength={200} style={inputStyle} /></Field>
              </>}
              <Field label="Nombre del producto">
                <input value={form.nombre_app ?? ''} onChange={e => setField('nombre_app', e.target.value || null)} placeholder={form.descripcion} style={inputStyle} />
              </Field>
              <Field label="Nombre para boleta (corto)">
                <input
                  value={form.nombre_boleta ?? ''}
                  onChange={e => setField('nombre_boleta', e.target.value || null)}
                  placeholder="ej: NITRATO DE AMONIO"
                  maxLength={30}
                  style={inputStyle} />
              </Field>
              <Field label="Descripción detallada" full>
                <input value={form.descripcion_detallada ?? ''} onChange={e => setField('descripcion_detallada', e.target.value || null)} style={inputStyle} />
              </Field>
              <Field label="Unidad de medida">
                <CatalogCombobox
                  value={unidadTexto}
                  onChange={setUnidadTexto}
                  options={unidades}
                  valueField="codigo"
                  placeholder="Buscar por código o nombre…"
                  emptyText="Puede escribir una unidad nueva"
                />
              </Field>
              <Field label="Stock actual (controlado por movimientos)">
                <input value={fmtN(form.stock_actual, 3)} disabled style={{ ...inputStyle, opacity: 0.6, textAlign: 'right' }} />
              </Field>
              <Field label="Stock mínimo">
                <input type="number" step="0.0001" value={form.stock_minimo ?? ''} onChange={e => setField('stock_minimo', e.target.value === '' ? null : Number(e.target.value))} style={inputStyle} />
              </Field>
              <Field label="Costo promedio (se calcula con compras en ERP)">
                <input value={fmtMoney(form.costo_promedio, form.moneda_costo)} disabled style={{ ...inputStyle, opacity: 0.6, textAlign: 'right' }} />
              </Field>
              <Field label="Moneda del costo">
                <select
                  value={form.moneda_costo || 'CRC'}
                  onChange={e => setField('moneda_costo', e.target.value as any)}
                  style={inputStyle}>
                  <option value="CRC">CRC - Colones</option>
                  <option value="USD">USD - Dolares</option>
                </select>
              </Field>
              <Field label="Precio venta (se gestiona en ERP)">
                <input value={fmtN(form.precio_venta, 2)} disabled style={{ ...inputStyle, opacity: 0.6, textAlign: 'right' }} />
              </Field>
              <Field label="Activo" full>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!form.activo} onChange={e => setField('activo', e.target.checked)} />
                  <span style={{ fontSize: '13px' }}>{form.activo ? 'Producto activo' : 'Producto desactivado'}</span>
                </label>
              </Field>
              <Field label="Pertenece a Aplicaciones" full>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!form.pertenece_aplicaciones} onChange={e => setField('pertenece_aplicaciones', e.target.checked)} />
                  <span style={{ fontSize: '13px' }}>{form.pertenece_aplicaciones ? 'Es un insumo real de Aplicaciones' : 'No es de Aplicaciones (empaque, otra área) — no sale en Depuración de Inventario'}</span>
                </label>
              </Field>
            </div>
          )}

          {tab === 'operativo' && (
            <div className="producto-modal-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px 18px' }}>
              <Field label="Modalidad de aplicación" full>
                <select
                  value={form.modalidad_aplicacion_id != null ? String(form.modalidad_aplicacion_id) : ''}
                  onChange={e => setField('modalidad_aplicacion_id', e.target.value ? Number(e.target.value) : null)}
                  style={inputStyle}>
                  <option value="">— Seleccionar —</option>
                  {modalidades.map(m => (
                    <option key={m.id} value={String(m.id)}>
                      {m.codigo} — {m.nombre}
                      {m.requiere_equipo ? ' · usa equipo' : ''}
                      {m.requiere_agua ? ' · usa agua' : ''}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Justificación / Catálogo" full>
                <input value={form.justificacion ?? ''} onChange={e => setField('justificacion', e.target.value || null)} placeholder="ej: NUTRICION, CONTROL ARVENSES, ADHERENTE PENETRANTE…" style={inputStyle} />
              </Field>
              <Field label="Departamento">
                <CatalogCombobox
                  value={departamentoTexto}
                  onChange={setDepartamentoTexto}
                  options={departamentos}
                  valueField="nombre"
                  placeholder="Buscar por código o nombre…"
                  emptyText="Puede escribir un departamento nuevo"
                />
              </Field>
              <Field label="Composición / Ingrediente activo" full>
                <CatalogCombobox
                  value={ingredienteTexto}
                  onChange={setIngredienteTexto}
                  options={ingredientesActivos}
                  valueField="nombre"
                  placeholder="Buscar por código o nombre…"
                  emptyText="Puede escribir una composición nueva"
                />
                <div style={{ fontSize: '10px', color: 'var(--ink-faint)', marginTop: '4px', fontStyle: 'italic' }}>
                  Para fertilizantes muestra la composición (ej: 62% K2O). Para agroquímicos, la molécula activa (ej: AMETRINA).
                </div>
              </Field>
              <div style={{ gridColumn: '1 / -1', padding: '8px 12px', background: 'var(--tone-info-bg)', border: '1px solid var(--tone-info-border)', borderRadius: '6px', fontSize: '11px', color: 'var(--tone-info-text)' }}>
                ℹ Los cambios de Justificación, Departamento, Composición / Ingrediente activo y Reingreso se propagan al Plan de Manejo y a las boletas existentes con este artículo al guardar.
              </div>
              <Field label="Latencia (días)">
                <input type="number" value={form.latencia_dias ?? ''} onChange={e => setField('latencia_dias', e.target.value === '' ? null : Number(e.target.value))} style={inputStyle} />
              </Field>
              <Field label="Reingreso">
                <input value={form.reingreso ?? ''} onChange={e => setField('reingreso', e.target.value || null)} placeholder="ej: N/A, 24h, 48h" style={inputStyle} />
              </Field>
              <Field label="Requiere autorización antes de despachar" full>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!form.requiere_autorizacion} onChange={e => setField('requiere_autorizacion', e.target.checked)} />
                  <span style={{ fontSize: '13px' }}>{form.requiere_autorizacion ? 'Requiere V°B° ingeniero' : 'Despacho libre'}</span>
                </label>
              </Field>
              <Field label="Observaciones" full>
                <textarea value={form.observaciones ?? ''} onChange={e => setField('observaciones', e.target.value || null)} rows={3} style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }} />
              </Field>
              <Field label="Notas agronómicas" full>
                <textarea value={form.notas_agronomicas ?? ''} onChange={e => setField('notas_agronomicas', e.target.value || null)} rows={3} placeholder="Compatibilidad, mezclas, recomendaciones técnicas…" style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }} />
              </Field>
            </div>
          )}

          {tab === 'gap' && (
            <div className="producto-modal-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px 18px' }}>
              <Field label="Registro MAG">
                <input value={form.registro_mag ?? ''} onChange={e => setField('registro_mag', e.target.value || null)} placeholder="ej: 6321-1" style={inputStyle} />
              </Field>
              <Field label="Vencimiento etiqueta">
                <input type="date" value={form.vencimiento_etiqueta ?? ''} onChange={e => setField('vencimiento_etiqueta', e.target.value || null)} style={inputStyle} />
              </Field>
              <Field label="Banda toxicológica">
                <select value={form.banda_toxicologica ?? ''} onChange={e => setField('banda_toxicologica', e.target.value || null)} style={inputStyle}>
                  <option value="">—</option>
                  <option value="Ia">Ia — Extremadamente tóxico</option>
                  <option value="Ib">Ib — Altamente tóxico</option>
                  <option value="II">II — Moderadamente tóxico</option>
                  <option value="III">III — Ligeramente tóxico</option>
                  <option value="IV">IV — Cuidado</option>
                </select>
              </Field>
              <Field label="Color de banda">
                <select value={form.banda_color ?? ''} onChange={e => setField('banda_color', e.target.value || null)} style={inputStyle}>
                  <option value="">—</option>
                  <option value="rojo">Rojo</option>
                  <option value="amarillo">Amarillo</option>
                  <option value="azul">Azul</option>
                  <option value="verde">Verde</option>
                </select>
              </Field>
              <Field label="URL etiqueta (PDF)" full>
                <input value={form.etiqueta_pdf_url ?? ''} onChange={e => setField('etiqueta_pdf_url', e.target.value || null)} placeholder="https://…" style={inputStyle} />
              </Field>
              <Field label="URL hoja seguridad MSDS (PDF)" full>
                <input value={form.msds_pdf_url ?? ''} onChange={e => setField('msds_pdf_url', e.target.value || null)} placeholder="https://…" style={inputStyle} />
              </Field>
            </div>
          )}

          {tab === 'seguridad' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '14px' }}>
              <Field label="EPI requerido">
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {EPI_OPCIONES.map(item => {
                    const seleccionado = (form.epi_requerido || []).includes(item);
                    return (
                      <button
                        key={item}
                        onClick={() => toggleEPI(item)}
                        style={{
                          padding: '7px 12px', borderRadius: '999px', fontSize: '12px', fontWeight: 700,
                          border: `1px solid ${seleccionado ? 'var(--emp-accent)' : 'var(--line)'}`,
                          background: seleccionado ? 'var(--emp-accent-bg)' : 'transparent',
                          color: seleccionado ? 'var(--emp-accent-txt)' : 'var(--ink-muted)',
                          cursor: 'pointer',
                        }}>{seleccionado ? '✓ ' : ''}{item}</button>
                    );
                  })}
                </div>
              </Field>
              <Field label="Restricciones climáticas">
                <textarea value={form.restricciones_clima ?? ''} onChange={e => setField('restricciones_clima', e.target.value || null)} rows={3} placeholder="ej: No aplicar con viento >15 km/h, evitar 2h antes/después de lluvia" style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }} />
              </Field>
              <Field label="Zona buffer (metros)">
                <input type="number" step="0.1" value={form.zona_buffer_m ?? ''} onChange={e => setField('zona_buffer_m', e.target.value === '' ? null : Number(e.target.value))} style={{ ...inputStyle, maxWidth: '200px' }} />
              </Field>
            </div>
          )}

          {tab === 'historial' && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '12px', fontSize: '12px', color: 'var(--ink-muted)' }}>
                <History size={14} /> Últimos cambios (más reciente primero)
              </div>
              {historial.length === 0 ? (
                <div style={{ padding: '20px', textAlign: 'center', color: 'var(--ink-muted)', fontSize: '13px' }}>
                  Sin cambios registrados todavía
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                  <thead>
                    <tr style={{ background: 'var(--surface-raised)' }}>
                      <th style={{ ...thStyle, padding: '8px' }}>Fecha</th>
                      <th style={{ ...thStyle, padding: '8px', textAlign: 'left' }}>Campo</th>
                      <th style={{ ...thStyle, padding: '8px', textAlign: 'left' }}>Antes</th>
                      <th style={{ ...thStyle, padding: '8px', textAlign: 'left' }}>Después</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historial.map(h => (
                      <tr key={h.id} style={{ borderTop: '1px solid var(--line-dim)' }}>
                        <td style={{ padding: '8px', fontFamily: 'monospace', fontSize: '11px', color: 'var(--ink-faint)' }}>
                          {new Date(h.cambiado_at).toLocaleString('es-CR')}
                        </td>
                        <td style={{ padding: '8px', fontWeight: 700 }}>{h.campo}</td>
                        <td style={{ padding: '8px', color: 'var(--tone-warn-text)', fontFamily: 'monospace', fontSize: '11px' }}>{h.valor_antes || '—'}</td>
                        <td style={{ padding: '8px', color: 'var(--tone-success-text)', fontFamily: 'monospace', fontSize: '11px' }}>{h.valor_despues || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="producto-modal-footer" style={{ padding: '14px 24px', borderTop: '1px solid var(--line)', display: 'flex', gap: '10px', justifyContent: 'flex-end', flexShrink: 0, background: 'var(--surface-raised)' }}>
          <button onClick={onClose} disabled={guardando} style={{
            minHeight: '40px', padding: '9px 17px', borderRadius: '9px', fontSize: '13px', fontWeight: 700,
            border: '1px solid var(--line)', background: 'var(--surface-overlay)', color: 'var(--ink-muted)',
            cursor: guardando ? 'not-allowed' : 'pointer',
          }}>Cancelar</button>
          {tab !== 'historial' && (
            <button onClick={guardar} disabled={guardando} style={{
              minHeight: '40px', padding: '9px 18px', borderRadius: '9px', fontSize: '13px', fontWeight: 800,
              border: 'none', background: guardando ? 'var(--line-dim)' : 'var(--emp-accent)',
              color: guardando ? 'var(--ink-faint)' : 'var(--emp-accent-on)',
              cursor: guardando ? 'not-allowed' : 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: '6px',
            }}>
              {guardando ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {guardando ? 'Guardando…' : (esNuevo ? 'Crear producto' : 'Guardar')}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

// ════════════════════════════════════════════════════════════════════
// Modal de trazabilidad — boletas donde se usó este artículo
// ════════════════════════════════════════════════════════════════════

interface TrazaFila {
  id: number;
  dosis: number | null;
  cantidad_aplicar: number | null;
  medida: string | null;
  excluida: boolean | null;
  boleta: {
    id: number;
    numero: string;
    semana: string | null;
    fecha_plan: string | null;
    fecha_aplicacion: string | null;
    lote_num: string | null;
    finca_nombre: string | null;
    etapa_nombre: string | null;
    ciclo_nombre: string | null;
    estado: string | null;
    area_ha: number | null;
    manual: boolean | null;
  } | null;
}

const ESTADO_LABEL: Record<string, { texto: string; bg: string; fg: string }> = {
  borrador: { texto: 'Borrador', bg: 'rgba(148,163,184,0.15)', fg: '#cbd5e1' },
  pendiente: { texto: 'Pendiente', bg: 'rgba(245,158,11,0.15)', fg: '#fbbf24' },
  aplicada: { texto: 'Aplicada', bg: 'rgba(34,197,94,0.15)', fg: '#4ade80' },
  cancelada: { texto: 'Cancelada', bg: 'rgba(239,68,68,0.15)', fg: '#fca5a5' },
};

function fmtFechaCorta(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso + 'T00:00:00');
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('es-CR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function ModalTrazabilidad({ producto, empresaId, onClose }: {
  producto: Producto; empresaId: number; onClose: () => void;
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filas, setFilas] = useState<TrazaFila[]>([]);
  const [filtroEstado, setFiltroEstado] = useState<'todos' | 'aplicada' | 'pendiente' | 'borrador' | 'cancelada'>('todos');

  useEffect(() => {
    const cerrarConEscape = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', cerrarConEscape);
    const overflowAnterior = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', cerrarConEscape);
      document.body.style.overflow = overflowAnterior;
    };
  }, [onClose]);

  useEffect(() => {
    let cancelado = false;
    (async () => {
      setLoading(true);
      setError('');
      const { data, error: err } = await supabase
        .from('app_boleta_productos')
        .select(`
          id, dosis, cantidad_aplicar, medida, excluida,
          boleta:app_boletas(id, numero, semana, fecha_plan, fecha_aplicacion, lote_num, finca_nombre, etapa_nombre, ciclo_nombre, estado, area_ha, manual)
        `)
        .eq('empresa_id', empresaId)
        .eq('articulo', producto.codigo)
        .eq('es_agua', false)
        .order('id', { ascending: false })
        .limit(500);
      if (cancelado) return;
      if (err) { setError(err.message); setLoading(false); return; }
      const rows = ((data ?? []) as any[]).map(r => ({ ...r, boleta: Array.isArray(r.boleta) ? r.boleta[0] : r.boleta })) as TrazaFila[];
      rows.sort((a, b) => (b.boleta?.fecha_plan || '').localeCompare(a.boleta?.fecha_plan || ''));
      setFilas(rows);
      setLoading(false);
    })();
    return () => { cancelado = true; };
  }, [producto.codigo, empresaId]);

  const filasFiltradas = useMemo(
    () => filtroEstado === 'todos' ? filas : filas.filter(f => f.boleta?.estado === filtroEstado),
    [filas, filtroEstado],
  );

  const resumen = useMemo(() => {
    const boletasUnicas = new Set(filasFiltradas.map(f => f.boleta?.id).filter(Boolean));
    const totalAplicado = filasFiltradas
      .filter(f => !f.excluida)
      .reduce((s, f) => s + (Number(f.cantidad_aplicar) || 0), 0);
    const medida = filasFiltradas.find(f => f.medida)?.medida || '';
    const fechas = filasFiltradas.map(f => f.boleta?.fecha_plan).filter(Boolean) as string[];
    const desde = fechas.length ? fechas.reduce((a, b) => a < b ? a : b) : null;
    const hasta = fechas.length ? fechas.reduce((a, b) => a > b ? a : b) : null;
    return { boletas: boletasUnicas.size, totalAplicado, medida, desde, hasta };
  }, [filasFiltradas]);

  return createPortal(
    <div onMouseDown={event => { if (event.target === event.currentTarget) onClose(); }} style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(2,6,23,0.78)', backdropFilter: 'blur(5px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
    }}>
      <div role="dialog" aria-modal="true" style={{
        width: '100%', maxWidth: '980px', maxHeight: '88vh', borderRadius: '18px',
        background: 'var(--surface-base)', border: '1px solid var(--line)', overflow: 'hidden',
        boxShadow: '0 28px 80px rgba(0,0,0,0.62)', display: 'flex', flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{ padding: '18px 22px', borderBottom: '1px solid var(--line)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', background: 'var(--surface-raised)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
            <div style={{ width: '38px', height: '38px', borderRadius: '11px', display: 'grid', placeItems: 'center', background: 'rgba(37,99,235,0.14)', border: '1px solid rgba(59,130,246,0.4)', color: '#93c5fd', flexShrink: 0 }}>
              <ListTree size={19} />
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: '10px', color: '#93c5fd', fontWeight: 800, marginBottom: '3px', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                Trazabilidad · Código {producto.codigo}
              </div>
              <h2 style={{ fontSize: '17px', lineHeight: 1.25, color: 'var(--ink)', fontWeight: 800, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {producto.nombre_app || producto.descripcion}
              </h2>
            </div>
          </div>
          <button aria-label="Cerrar" title="Cerrar (Esc)" onClick={onClose} style={{ width: '34px', height: '34px', borderRadius: '9px', display: 'grid', placeItems: 'center', background: 'var(--surface-overlay)', border: '1px solid var(--line)', color: 'var(--ink-muted)', cursor: 'pointer', flexShrink: 0 }}>
            <X size={18} />
          </button>
        </div>

        {/* Resumen + filtro */}
        <div style={{ padding: '14px 22px', borderBottom: '1px solid var(--line)', display: 'flex', alignItems: 'center', gap: '18px', flexWrap: 'wrap', background: 'var(--surface-base)' }}>
          <SummaryStat label="Boletas" value={String(resumen.boletas)} />
          <SummaryStat label={`Total aplicado${resumen.medida ? ` (${resumen.medida})` : ''}`} value={fmtN(resumen.totalAplicado, 2)} />
          <SummaryStat label="Rango de fechas" value={resumen.desde && resumen.hasta ? `${fmtFechaCorta(resumen.desde)} — ${fmtFechaCorta(resumen.hasta)}` : '—'} />
          <select
            value={filtroEstado}
            onChange={e => setFiltroEstado(e.target.value as any)}
            style={{
              marginLeft: 'auto', padding: '7px 10px', borderRadius: '8px', border: '1px solid var(--line)',
              background: 'var(--surface-overlay)', color: 'var(--ink)', fontSize: '12px',
            }}>
            <option value="todos">Todos los estados</option>
            <option value="aplicada">Aplicada</option>
            <option value="pendiente">Pendiente</option>
            <option value="borrador">Borrador</option>
            <option value="cancelada">Cancelada</option>
          </select>
        </div>

        {/* Contenido */}
        <div style={{ overflowY: 'auto', flex: 1, background: 'var(--surface-base)' }}>
          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: 'var(--ink-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
              <Loader2 size={16} className="animate-spin" /> Cargando trazabilidad…
            </div>
          ) : error ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#fca5a5', fontSize: '13px' }}>{error}</div>
          ) : filasFiltradas.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: 'var(--ink-muted)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
              <FileSpreadsheet size={26} style={{ opacity: 0.5 }} />
              <div style={{ fontSize: '13px' }}>Este producto no aparece en ninguna boleta {filtroEstado !== 'todos' ? `en estado "${filtroEstado}"` : 'todavía'}.</div>
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12.5px' }}>
              <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                <tr style={{ background: 'var(--surface-raised)' }}>
                  <th style={thStyle}>Boleta</th>
                  <th style={thStyle}>Fecha</th>
                  <th style={{ ...thStyle, textAlign: 'left' }}>Lote / Finca</th>
                  <th style={{ ...thStyle, textAlign: 'left' }}>Etapa · Ciclo</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Dosis/ha</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Aplicado</th>
                  <th style={thStyle}>Estado</th>
                </tr>
              </thead>
              <tbody>
                {filasFiltradas.map(f => {
                  const b = f.boleta;
                  const estado = ESTADO_LABEL[b?.estado || ''] || { texto: b?.estado || '—', bg: 'rgba(148,163,184,0.15)', fg: '#cbd5e1' };
                  return (
                    <tr key={f.id} style={{ borderTop: '1px solid var(--line-dim)', opacity: f.excluida ? 0.5 : 1 }}>
                      <td style={{ ...tdMonoStyle, fontSize: '11px' }}>
                        {b?.numero ?? '—'}
                        {b?.manual && <div style={{ fontSize: '9px', color: '#fbbf24', fontWeight: 800 }}>MANUAL</div>}
                        {f.excluida && <div style={{ fontSize: '9px', color: '#fca5a5', fontWeight: 800 }}>EXCLUIDA</div>}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'center', fontSize: '11px', color: 'var(--ink-muted)' }}>
                        {fmtFechaCorta(b?.fecha_aplicacion || b?.fecha_plan)}
                      </td>
                      <td style={tdStyle}>
                        <div style={{ fontWeight: 700 }}>{b?.lote_num ? `Lote ${b.lote_num}` : '—'}</div>
                        <div style={{ fontSize: '11px', color: 'var(--ink-muted)' }}>{b?.finca_nombre || ''}</div>
                      </td>
                      <td style={tdStyle}>
                        <div>{b?.etapa_nombre || '—'}</div>
                        <div style={{ fontSize: '11px', color: 'var(--ink-muted)' }}>{b?.ciclo_nombre || ''}</div>
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'monospace' }}>{fmtN(f.dosis, 3)}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'monospace', fontWeight: 700 }}>
                        {fmtN(f.cantidad_aplicar, 2)} {f.medida || ''}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>
                        <span style={{ padding: '2px 8px', borderRadius: '999px', fontSize: '10px', fontWeight: 800, background: estado.bg, color: estado.fg }}>
                          {estado.texto}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {filas.length >= 500 && (
          <div style={{ padding: '8px 22px', fontSize: '11px', color: 'var(--ink-faint)', borderTop: '1px solid var(--line)', background: 'var(--surface-raised)' }}>
            Mostrando las últimas 500 líneas. Puede haber uso histórico anterior no incluido.
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

function SummaryStat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div style={{ fontSize: '10px', color: 'var(--ink-faint)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
      <div style={{ fontSize: '15px', fontWeight: 800, color: 'var(--ink)', fontFamily: 'monospace' }}>{value}</div>
    </div>
  );
}

type CatalogOption = { id: number; codigo: string; nombre: string };

function CatalogCombobox({
  value,
  onChange,
  options,
  valueField,
  placeholder,
  emptyText,
}: {
  value: string;
  onChange: (value: string) => void;
  options: CatalogOption[];
  valueField: 'codigo' | 'nombre';
  placeholder: string;
  emptyText: string;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const listboxId = useId();
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const normalizedQuery = value.trim().toLocaleLowerCase('es');
  const filtered = useMemo(() => {
    if (!normalizedQuery) return options.slice(0, 10);
    return options
      .filter(option =>
        option.codigo.toLocaleLowerCase('es').includes(normalizedQuery)
        || option.nombre.toLocaleLowerCase('es').includes(normalizedQuery)
      )
      .slice(0, 10);
  }, [normalizedQuery, options]);

  useEffect(() => {
    const closeOnOutside = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', closeOnOutside);
    return () => document.removeEventListener('mousedown', closeOnOutside);
  }, []);

  useEffect(() => setActiveIndex(0), [normalizedQuery]);

  const selectOption = (option: CatalogOption) => {
    onChange(option[valueField]);
    setOpen(false);
  };

  return (
    <div ref={rootRef} style={{ position: 'relative' }}>
      <div style={{ position: 'relative' }}>
        <Search
          size={16}
          aria-hidden="true"
          style={{
            position: 'absolute', left: '12px', top: '50%',
            transform: 'translateY(-50%)', color: 'var(--ink-faint)',
            pointerEvents: 'none',
          }}
        />
        <input
          role="combobox"
          aria-autocomplete="list"
          aria-controls={listboxId}
          aria-expanded={open}
          value={value}
          onFocus={() => setOpen(true)}
          onChange={event => {
            onChange(event.target.value);
            setOpen(true);
          }}
          onKeyDown={event => {
            if (event.key === 'ArrowDown') {
              event.preventDefault();
              setOpen(true);
              setActiveIndex(index => Math.min(index + 1, filtered.length - 1));
            } else if (event.key === 'ArrowUp') {
              event.preventDefault();
              setActiveIndex(index => Math.max(index - 1, 0));
            } else if (event.key === 'Enter' && open && filtered[activeIndex]) {
              event.preventDefault();
              selectOption(filtered[activeIndex]);
            } else if (event.key === 'Escape') {
              setOpen(false);
            }
          }}
          placeholder={placeholder}
          style={{ ...inputStyle, paddingLeft: '38px', paddingRight: '38px' }}
        />
        <button
          type="button"
          tabIndex={-1}
          aria-label={open ? 'Cerrar opciones' : 'Mostrar opciones'}
          onMouseDown={event => event.preventDefault()}
          onClick={() => setOpen(current => !current)}
          style={{
            position: 'absolute', right: '6px', top: '50%',
            width: '30px', height: '30px', transform: 'translateY(-50%)',
            display: 'grid', placeItems: 'center', border: 0,
            background: 'transparent', color: 'var(--ink-muted)', cursor: 'pointer',
          }}
        >
          <ChevronDown size={16} style={{ transform: open ? 'rotate(180deg)' : undefined, transition: 'transform 120ms ease' }} />
        </button>
      </div>

      {open && (
        <div
          id={listboxId}
          role="listbox"
          style={{
            position: 'absolute', zIndex: 30, top: 'calc(100% + 6px)', left: 0, right: 0,
            maxHeight: '240px', overflowY: 'auto', padding: '6px',
            borderRadius: '11px', border: '1px solid var(--line)',
            background: 'var(--surface-raised)',
            boxShadow: '0 16px 36px rgba(2, 6, 23, 0.28)',
          }}
        >
          {filtered.length > 0 ? filtered.map((option, index) => (
            <button
              type="button"
              role="option"
              aria-selected={option[valueField] === value}
              key={option.id}
              onMouseDown={event => event.preventDefault()}
              onMouseEnter={() => setActiveIndex(index)}
              onClick={() => selectOption(option)}
              style={{
                width: '100%', minHeight: '42px', padding: '8px 10px',
                display: 'grid', gridTemplateColumns: '70px 1fr', alignItems: 'center',
                gap: '10px', textAlign: 'left', border: 0, borderRadius: '8px',
                background: index === activeIndex ? 'var(--emp-accent-bg)' : 'transparent',
                color: 'var(--ink)', cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              <span style={{ color: 'var(--emp-accent-txt)', fontSize: '11px', fontWeight: 800 }}>
                {option.codigo}
              </span>
              <span style={{ fontSize: '13px', fontWeight: 700 }}>{option.nombre}</span>
            </button>
          )) : (
            <div style={{ padding: '12px', color: 'var(--ink-muted)', fontSize: '12px' }}>
              Sin coincidencias. {emptyText}.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%', minHeight: '42px', padding: '9px 11px', borderRadius: '9px',
  border: '1px solid var(--line)', background: 'var(--surface-overlay)', color: 'var(--ink)',
  fontSize: '13px', outline: 'none', boxSizing: 'border-box',
};

function Field({ label, children, full }: { label: string; children: React.ReactNode; full?: boolean }) {
  return (
    <div style={{ gridColumn: full ? '1 / -1' : undefined, minWidth: 0 }}>
      <div style={{ fontSize: '11px', fontWeight: 800, color: 'var(--ink-muted)', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.055em' }}>{label}</div>
      {children}
    </div>
  );
}

export default CatalogoProductosPage;
